import type { TaskAmountExtract } from '../types';

/**
 * Where each filed value came from, as Reconcile states it on save (grading-loop spec 2026-10-22 §2). The suggestion is
 * graded against what the preparer FILED, so a value that came from the draft must never count as their independent
 * answer: that is the suggestion grading itself.
 *
 * Reconcile seeds its inputs from the filed figures only (`filedAmounts`: filed, else the legacy `amounts` bag), never
 * from the draft (ruling 2026-10-26, DP-GRADE-PREFILL). So, per amount key:
 *   - changed from what the form was seeded with, or typed into an empty input → `typed`;
 *   - unchanged, seeded from previously SAVED filed figures → whatever origin those had (carried forward, so a value
 *     saved as `prefilled-unchanged` before the ruling stays not-independent; omitted if none was recorded);
 *   - unchanged, seeded from the legacy `amounts` bag → omitted (unrecorded: not the draft, not known to be typed);
 *   - no value → omitted.
 * Equality is within half a cent, the grade's own tolerance.
 */
export type FiledOrigin = 'typed' | 'prefilled-unchanged' | 'extracted';

const KNOWN: readonly string[] = ['typed', 'prefilled-unchanged', 'extracted'];

export function filedOriginFor(
  extract: TaskAmountExtract | null | undefined,
  amounts: Record<string, number | null>,
  keys: readonly string[],
): Record<string, FiledOrigin> {
  const filed = extract?.filed ?? {};
  const seededFromFiled = Object.keys(filed).length > 0;
  const seed = seededFromFiled ? filed : (extract?.amounts ?? {});

  const out: Record<string, FiledOrigin> = {};
  for (const key of keys) {
    const value = amounts[key];
    if (value === null || value === undefined) continue;
    const seeded = seed[key];
    const unchanged = seeded !== null && seeded !== undefined && Math.abs(value - seeded) < 0.005;
    if (!unchanged) {
      out[key] = 'typed';
    } else if (seededFromFiled) {
      const prior = extract?.filedOrigin?.[key];
      if (prior && KNOWN.includes(prior)) out[key] = prior as FiledOrigin;
    }
  }
  return out;
}
